import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useSidebar } from '../../contexts/SidebarContext';
import MilestoneWorkflowModal from './components/MilestoneWorkflowModal';
import { fetchMilestoneTemplateActionsByMilestoneIds } from '../../services/workflowAdminService';
import {
  fetchMilestoneNameOptionsCatalog,
  fetchTemplateNameOptions,
  replaceMilestoneTemplateNameOptions,
} from '../../services/milestoneNameOptionsService';
import { userRoleService } from '../../services/userRoleService';
import { WORKFLOW_ROLE_TYPES } from '../../workflow/actionRegistry';

// ─────────────────────────────────────────────
// Reusable Modal wrapper
// ─────────────────────────────────────────────
const Modal = ({ isOpen, onClose, title, children, footer, maxWidthClass = 'sm:max-w-xl' }) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/50 animate-fade-in"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="phase-template-modal-title"
        className={`bg-card border border-border shadow-xl w-full ${maxWidthClass} max-h-[min(92dvh,92vh)] flex flex-col min-h-0 overflow-hidden animate-slide-in rounded-t-2xl sm:rounded-lg`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between shrink-0 gap-3 px-4 py-3 sm:px-6 sm:py-4 border-b border-border">
          <h2
            id="phase-template-modal-title"
            className="text-base sm:text-lg font-semibold text-foreground min-w-0"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Fermer"
          >
            <Icon name="X" size={20} />
          </button>
        </div>
        <div className="modal-scroll flex-1 min-h-0 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
          {children}
        </div>
        {footer ? (
          <div className="shrink-0 border-t border-border bg-card px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-4">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Input field helper
// ─────────────────────────────────────────────
const inlineFieldGridStyle = { gridTemplateColumns: '8.25rem 1fr' };

/** Libellé et contrôle sur la même ligne (2 colonnes). */
const InlineFieldRow = ({ label, children, required, align = 'center', hint, hintId }) => (
  <div className="mb-3">
    <div
      className={`grid gap-x-3 min-w-0 ${align === 'start' ? 'items-start' : 'items-center'}`}
      style={inlineFieldGridStyle}
    >
      <label className="text-sm font-medium text-foreground shrink-0">
        {label} {required && <span className="text-destructive">*</span>}
      </label>
      <div className="min-w-0">{children}</div>
    </div>
    {hint ? (
      <p
        id={hintId}
        className="mt-1 text-[11px] text-muted-foreground leading-snug"
        style={{ marginLeft: 'calc(8.25rem + 0.75rem)' }}
      >
        {hint}
      </p>
    ) : null}
  </div>
);

const inputClass =
  'w-full px-3 py-2 bg-background border border-border rounded-md text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors';

const normalizeVisibleRoleTypes = (roles) =>
  Array.isArray(roles) ? roles.filter((role) => typeof role === 'string' && role.trim() !== '') : [];

const roleLabel = (role) =>
  ({
    administrator: 'Administrateur',
    dentist_client: 'Dentiste client',
    dentist_supplier: 'Prothésiste / fournisseur',
    operator_3d: 'Opérateur 3D',
    operator_thermo: 'Opérateur thermo',
    operator_finition: 'Opérateur finition',
    operator_qualite: 'Opérateur qualité',
    livraison: 'Livraison / expédition',
    production_responsible: 'Resp. production',
    logistics_coordinator: 'Coord. logistique',
    distributor: 'Distributeur',
    production_supplier: 'Fournisseur production',
  }[role] || role);

const VisibilityRolesField = ({ value, onChange, inputIdPrefix, roleOptions }) => {
  const selectedRoles = normalizeVisibleRoleTypes(value);

  const options = useMemo(() => {
    const sel = normalizeVisibleRoleTypes(value);
    const byType = new Map((roleOptions || []).map((o) => [o.roleType, o]));
    for (const t of sel) {
      if (!byType.has(t)) byType.set(t, { roleType: t, roleName: roleLabel(t) });
    }
    return [...byType.values()].sort((a, b) =>
      String(a.roleName || roleLabel(a.roleType)).localeCompare(
        String(b.roleName || roleLabel(b.roleType)),
        'fr',
        { sensitivity: 'base' }
      )
    );
  }, [roleOptions, value]);

  const toggleRole = (role) => {
    if (selectedRoles.includes(role)) {
      onChange(selectedRoles.filter((item) => item !== role));
      return;
    }
    onChange([...selectedRoles, role]);
  };

  return (
    <div className="min-w-0">
      <p className="text-[11px] text-muted-foreground mb-1.5">
        Afficher dans case-detail pour ces rôles
      </p>
      <div className="modal-scroll grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 max-h-36 sm:max-h-40 pr-0.5">
        {options.map(({ roleType, roleName }) => {
          const inputId = `${inputIdPrefix}-${roleType}`;
          const checked = selectedRoles.includes(roleType);
          const displayName = (typeof roleName === 'string' && roleName.trim() !== '')
            ? roleName
            : roleLabel(roleType);
          return (
            <label
              key={roleType}
              htmlFor={inputId}
              className="flex items-center gap-1.5 py-0.5 text-xs leading-snug text-foreground cursor-pointer"
            >
              <input
                id={inputId}
                type="checkbox"
                checked={checked}
                onChange={() => toggleRole(roleType)}
                className="w-3.5 h-3.5 shrink-0 rounded border-border text-primary"
              />
              <span className="min-w-0 truncate" title={displayName}>
                {displayName}
              </span>
            </label>
          );
        })}
      </div>
      <p className="mt-1.5 text-[11px] text-muted-foreground leading-snug">
        Laisser vide pour conserver le comportement actuel et afficher cet élément a tous les rôles.
      </p>
    </div>
  );
};

// ─────────────────────────────────────────────
// Delete confirmation modal
// ─────────────────────────────────────────────
const DUPLICATE_PHASE_SUFFIX = ' (copie)';

function phaseNameForDuplicate(sourceName) {
  const base = String(sourceName || '')
    .replace(/\s*\(copie\)\s*$/i, '')
    .trim();
  return base ? `${base}${DUPLICATE_PHASE_SUFFIX}` : `Phase${DUPLICATE_PHASE_SUFFIX}`;
}

// ─────────────────────────────────────────────
// Duplicate phase: with or without milestone templates (+ workflow bindings)
// ─────────────────────────────────────────────
const DuplicatePhaseModal = ({ isOpen, onClose, phase, onChoose, isLoading }) => (
  <Modal isOpen={isOpen} onClose={onClose} title="Dupliquer la phase">
    <p className="text-sm text-muted-foreground mb-1">
      Une copie sera créée en <strong className="text-foreground">fin de parcours</strong> (prochain ordre disponible),
      avec le nom :
    </p>
    <p className="text-sm font-medium text-foreground mb-4 rounded-md border border-border bg-muted/30 px-3 py-2">
      {phase ? phaseNameForDuplicate(phase.phase_name) : '—'}
    </p>
    <p className="text-xs text-muted-foreground mb-4">
      « Avec jalons » recopie aussi les liaisons <strong className="text-foreground">workflow</strong> (actions par jalon).
    </p>
    <div className="flex flex-col gap-2">
      <Button
        type="button"
        variant="default"
        fullWidth
        disabled={isLoading}
        iconName="Copy"
        iconPosition="left"
        onClick={() => onChoose(true)}
      >
        Dupliquer avec les jalons
      </Button>
      <Button
        type="button"
        variant="outline"
        fullWidth
        disabled={isLoading}
        onClick={() => onChoose(false)}
      >
        Dupliquer sans les jalons
      </Button>
      <Button type="button" variant="ghost" fullWidth disabled={isLoading} onClick={onClose}>
        Annuler
      </Button>
    </div>
  </Modal>
);

const DeleteModal = ({ isOpen, onClose, onConfirm, itemName, isLoading }) => (
  <Modal isOpen={isOpen} onClose={onClose} title="Confirmer la suppression">
    <div className="flex items-center justify-center w-14 h-14 rounded-full bg-destructive/10 mx-auto mb-4">
      <Icon name="Trash2" size={28} className="text-destructive" />
    </div>
    <p className="text-center text-muted-foreground mb-6">
      Êtes-vous sûr de vouloir supprimer <strong className="text-foreground">"{itemName}"</strong> ?
      Cette action est irréversible.
    </p>
    <div className="flex gap-3">
      <Button variant="outline" fullWidth onClick={onClose} disabled={isLoading}>
        Annuler
      </Button>
      <Button variant="destructive" fullWidth onClick={onConfirm} disabled={isLoading}>
        {isLoading ? 'Suppression...' : 'Supprimer'}
      </Button>
    </div>
  </Modal>
);

// ─────────────────────────────────────────────
// Phase Form Modal
// ─────────────────────────────────────────────
const PhaseFormModal = ({ isOpen, onClose, onSave, phase, isLoading, workflowRoleOptions }) => {
  const isEdit = !!phase?.id;
  const [form, setForm] = useState({
    phase_name: '',
    phase_order: '',
    description: '',
    responsible_team: '',
    group: '',
    is_active: true,
    case_detail_visible_role_types: [],
  });

  useEffect(() => {
    if (phase) {
      setForm({
        phase_name: phase.phase_name || '',
        phase_order: phase.phase_order ?? '',
        description: phase.description || '',
        responsible_team: phase.responsible_team || '',
        group: phase.group || '',
        is_active: phase.is_active !== false,
        case_detail_visible_role_types: normalizeVisibleRoleTypes(phase.case_detail_visible_role_types),
      });
    } else {
      setForm({
        phase_name: '',
        phase_order: '',
        description: '',
        responsible_team: '',
        group: '',
        is_active: true,
        case_detail_visible_role_types: [],
      });
    }
  }, [phase, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...form, phase_order: parseInt(form.phase_order) });
  };

  const formId = 'phase-template-form';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Modifier la phase' : 'Nouvelle phase'}
      footer={
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
          <Button variant="outline" fullWidth type="button" onClick={onClose} disabled={isLoading}>
            Annuler
          </Button>
          <Button variant="default" fullWidth type="submit" form={formId} disabled={isLoading}>
            {isLoading ? 'Enregistrement...' : isEdit ? 'Modifier' : 'Créer'}
          </Button>
        </div>
      }
    >
      <form id={formId} onSubmit={handleSubmit}>
        <InlineFieldRow label="Nom de la phase" required>
          <input
            className={inputClass}
            value={form.phase_name}
            onChange={(e) => setForm((f) => ({ ...f, phase_name: e.target.value }))}
            placeholder="Ex: Révision Initiale"
            required
          />
        </InlineFieldRow>
        <InlineFieldRow label="Ordre" required>
          <input
            className={inputClass}
            type="number"
            min="1"
            value={form.phase_order}
            onChange={(e) => setForm((f) => ({ ...f, phase_order: e.target.value }))}
            placeholder="1"
            required
          />
        </InlineFieldRow>
        <InlineFieldRow
          label="Groupe (phase)"
          hint="Regroupe les phases d’un même parcours (ex. soumission, fil d’aligneurs). Laisser vide si non utilisé."
          hintId="phase-group-hint"
        >
          <input
            className={inputClass}
            value={form.group}
            onChange={(e) => setForm((f) => ({ ...f, group: e.target.value }))}
            placeholder="1"
            aria-describedby="phase-group-hint"
          />
        </InlineFieldRow>
        <InlineFieldRow label="Statut">
          <label htmlFor="phase-active" className="inline-flex items-center gap-2 text-sm text-foreground cursor-pointer">
            <input
              type="checkbox"
              id="phase-active"
              checked={form.is_active}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              className="w-4 h-4 rounded border-border text-primary"
            />
            Active
          </label>
        </InlineFieldRow>
        <InlineFieldRow label="Visibilité" align="start">
          <VisibilityRolesField
            value={form.case_detail_visible_role_types}
            onChange={(value) => setForm((f) => ({ ...f, case_detail_visible_role_types: value }))}
            inputIdPrefix="phase-visible-role"
            roleOptions={workflowRoleOptions}
          />
        </InlineFieldRow>
        <InlineFieldRow label="Description">
          <input
            className={inputClass}
            type="text"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Description de la phase..."
          />
        </InlineFieldRow>
      </form>
    </Modal>
  );
};

// ─────────────────────────────────────────────
// Milestone Form Modal
// ─────────────────────────────────────────────
const MilestoneFormModal = ({
  isOpen,
  onClose,
  onSave,
  milestone,
  phases,
  isLoading,
  workflowRoleOptions,
  nameOptionsCatalog = [],
}) => {
  const isEdit = !!milestone?.id;
  const [form, setForm] = useState({
    phase_template_id: '',
    milestone_name: '',
    milestone_order: '',
    description: '',
    is_active: true,
    case_detail_visible_role_types: [],
    selectedOptionIds: [],
  });

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    (async () => {
      if (milestone?.id) {
        const linked = await fetchTemplateNameOptions(milestone.id);
        if (cancelled) return;
        setForm({
          phase_template_id: milestone.phase_template_id || '',
          milestone_name: milestone.milestone_name || '',
          milestone_order: milestone.milestone_order ?? '',
          description: milestone.description || '',
          is_active: milestone.is_active !== false,
          case_detail_visible_role_types: normalizeVisibleRoleTypes(milestone.case_detail_visible_role_types),
          selectedOptionIds: linked.map((o) => o.id),
        });
      } else {
        setForm({
          phase_template_id: milestone?.phase_template_id || '',
          milestone_name: '',
          milestone_order: '',
          description: '',
          is_active: true,
          case_detail_visible_role_types: [],
          selectedOptionIds: [],
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [milestone, isOpen]);

  const toggleOptionId = (optionId) => {
    setForm((f) => {
      const set = new Set(f.selectedOptionIds);
      if (set.has(optionId)) set.delete(optionId);
      else set.add(optionId);
      return { ...f, selectedOptionIds: [...set] };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.selectedOptionIds.length) {
      alert('Sélectionnez au moins une valeur autorisée.');
      return;
    }
    if (form.selectedOptionIds.length > 1 && !form.milestone_name.trim()) {
      alert('Indiquez un libellé générique affiché avant le choix (ex. Mode de découpe).');
      return;
    }
    onSave({ ...form, milestone_order: parseInt(form.milestone_order, 10) });
  };

  const formId = 'milestone-template-form';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Modifier le jalon' : 'Nouveau jalon'}
      footer={
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
          <Button variant="outline" fullWidth type="button" onClick={onClose} disabled={isLoading}>
            Annuler
          </Button>
          <Button variant="default" fullWidth type="submit" form={formId} disabled={isLoading}>
            {isLoading ? 'Enregistrement...' : isEdit ? 'Modifier' : 'Créer'}
          </Button>
        </div>
      }
    >
      <form id={formId} onSubmit={handleSubmit}>
        <InlineFieldRow label="Phase parente" required>
          <select
            className={inputClass}
            value={form.phase_template_id}
            onChange={(e) => setForm((f) => ({ ...f, phase_template_id: e.target.value }))}
            required
          >
            <option value="">Sélectionner une phase</option>
            {phases.map((p) => (
              <option key={p.id} value={p.id}>
                {p.phase_order}. {p.phase_name} - (Groupe {p.group})
              </option>
            ))}
          </select>
        </InlineFieldRow>
        <InlineFieldRow label="Libellé du jalon" required={form.selectedOptionIds.length > 1}>
          <input
            className={inputClass}
            value={form.milestone_name}
            onChange={(e) => setForm((f) => ({ ...f, milestone_name: e.target.value }))}
            placeholder={
              form.selectedOptionIds.length > 1
                ? 'Ex: Mode de découpe (affiché avant le choix)'
                : 'Ex: Validation du scan'
            }
            required={form.selectedOptionIds.length > 1}
          />
          {form.selectedOptionIds.length <= 1 ? (
            <p className="text-xs text-muted-foreground mt-1">
              Si une seule valeur est sélectionnée ci-dessous, le libellé sera aligné automatiquement.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground mt-1">
              Obligatoire lorsque plusieurs valeurs sont proposées : l&apos;utilisateur choisira Laser, Manuel, etc. à la validation.
            </p>
          )}
        </InlineFieldRow>
        <InlineFieldRow label="Valeurs autorisées" align="start" required>
          <div className="space-y-2 max-h-48 overflow-y-auto border border-border rounded-md p-3 bg-muted/20">
            {nameOptionsCatalog.length === 0 ? (
              <p className="text-xs text-muted-foreground">Aucune option dans le catalogue.</p>
            ) : (
              nameOptionsCatalog.map((opt) => (
                <label
                  key={opt.id}
                  className="flex items-center gap-2 text-sm cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={form.selectedOptionIds.includes(opt.id)}
                    onChange={() => toggleOptionId(opt.id)}
                    className="rounded border-border"
                  />
                  <span>{opt.label}</span>
                  {opt.category ? (
                    <span className="text-[10px] text-muted-foreground">({opt.category})</span>
                  ) : null}
                </label>
              ))
            )}
          </div>
        </InlineFieldRow>
        <InlineFieldRow label="Ordre" required>
          <input
            className={inputClass}
            type="number"
            min="1"
            value={form.milestone_order}
            onChange={(e) => setForm((f) => ({ ...f, milestone_order: e.target.value }))}
            placeholder="Ex: 1"
            required
          />
        </InlineFieldRow>
        <InlineFieldRow label="Statut">
          <label
            htmlFor="milestone-active"
            className="inline-flex items-center gap-2 text-sm text-foreground cursor-pointer"
          >
            <input
              type="checkbox"
              id="milestone-active"
              checked={form.is_active}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              className="w-4 h-4 rounded border-border text-primary"
            />
            Actif
          </label>
        </InlineFieldRow>
        <InlineFieldRow label="Visibilité" align="start">
          <VisibilityRolesField
            value={form.case_detail_visible_role_types}
            onChange={(value) => setForm((f) => ({ ...f, case_detail_visible_role_types: value }))}
            inputIdPrefix="milestone-visible-role"
            roleOptions={workflowRoleOptions}
          />
        </InlineFieldRow>
        <InlineFieldRow label="Description">
          <input
            className={inputClass}
            type="text"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Description du jalon..."
          />
        </InlineFieldRow>
      </form>
    </Modal>
  );
};

// ─────────────────────────────────────────────
// Badge helpers
// ─────────────────────────────────────────────
const ActiveBadge = ({ active }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
      active
        ? 'bg-success/10 text-success border border-success/20'
        : 'bg-muted text-muted-foreground border border-border'
    }`}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-success' : 'bg-muted-foreground'}`} />
    {active ? 'Actif' : 'Inactif'}
  </span>
);

const GroupBadge = ({ group }) => {
  if (!group) return <span className="text-muted-foreground text-sm">—</span>;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
      {group}
    </span>
  );
};

// ─────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────
const PhaseTemplatesPage = () => {
  const { isCollapsed, toggleSidebar } = useSidebar();

  // Data
  const [phases, setPhases] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Vue principale : parcours (phases + jalons) ; listes = tableaux ; workflow = vue plate des actions
  const [viewMode, setViewMode] = useState('journey'); // 'journey' | 'lists' | 'workflow'
  const [listSubTab, setListSubTab] = useState('phases'); // 'phases' | 'milestones' (si viewMode === 'lists')

  // Search
  const [search, setSearch] = useState('');

  // Expanded phase rows (to show milestones inline)
  const [expandedPhaseId, setExpandedPhaseId] = useState(null);

  // Phase modals
  const [phaseModal, setPhaseModal] = useState({ isOpen: false, phase: null });
  const [deletePhaseModal, setDeletePhaseModal] = useState({ isOpen: false, phase: null });
  const [duplicatePhaseModal, setDuplicatePhaseModal] = useState({ isOpen: false, phase: null });

  // Milestone modals
  const [milestoneModal, setMilestoneModal] = useState({ isOpen: false, milestone: null });
  const [deleteMilestoneModal, setDeleteMilestoneModal] = useState({ isOpen: false, milestone: null });

  // Workflow tab: binding counts + configure modal
  const [actionBindingCounts, setActionBindingCounts] = useState({});
  const [workflowModal, setWorkflowModal] = useState({
    isOpen: false,
    milestone: null,
    phaseLabel: '',
  });

  /** Catalogue milestone_name_options pour le formulaire jalon */
  const [nameOptionsCatalog, setNameOptionsCatalog] = useState([]);

  /** Rôles actifs depuis la table user_roles — alimente visibilité + workflow modal */
  const [workflowRoleCatalog, setWorkflowRoleCatalog] = useState([]);

  const workflowRoleOptions = useMemo(() => {
    if (workflowRoleCatalog.length > 0) return workflowRoleCatalog;
    return WORKFLOW_ROLE_TYPES.map((roleType) => ({
      roleType,
      roleName: roleLabel(roleType),
    }));
  }, [workflowRoleCatalog]);

  // ── Toast helper ─────────────────────────────────────────────────────
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Fetch ─────────────────────────────────────────────────────────────
  const fetchPhases = useCallback(async () => {
    const { data, error } = await supabase
      .from('phase_templates')
      .select('*')
      .order('phase_order', { ascending: true });
    if (error) { console.error(error); return; }
    setPhases(data || []);
  }, []);

  const fetchMilestones = useCallback(async () => {
    const { data, error } = await supabase
      .from('milestone_templates')
      .select(`
        *,
        phase:phase_templates(id, phase_name, phase_order)
      `)
      .order('milestone_order', { ascending: true });
    if (error) { console.error(error); return; }
    setMilestones(data || []);
  }, []);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([fetchPhases(), fetchMilestones()]);
    setIsLoading(false);
  }, [fetchPhases, fetchMilestones]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    fetchMilestoneNameOptionsCatalog()
      .then(setNameOptionsCatalog)
      .catch(console.error);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all = await userRoleService.getAllRoles();
        if (cancelled) return;
        const opts = (all || [])
          .filter((r) => r?.isActive && typeof r.roleType === 'string' && r.roleType.trim() !== '')
          .map((r) => ({ roleType: r.roleType.trim(), roleName: r.roleName }))
          .sort((a, b) =>
            String(a.roleName || a.roleType).localeCompare(String(b.roleName || b.roleType), 'fr', {
              sensitivity: 'base',
            })
          );
        setWorkflowRoleCatalog(opts);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!milestones.length) {
      setActionBindingCounts({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ids = milestones.map((m) => m.id);
        const rows = await fetchMilestoneTemplateActionsByMilestoneIds(ids);
        if (cancelled) return;
        const counts = {};
        for (const r of rows) {
          counts[r.milestone_template_id] = (counts[r.milestone_template_id] || 0) + 1;
        }
        setActionBindingCounts(counts);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [milestones]);

  // ── Phase CRUD ────────────────────────────────────────────────────────
  const handleSavePhase = async (formData) => {
    setIsSaving(true);
    try {
      const payload = {
        phase_name: formData.phase_name,
        phase_order: formData.phase_order,
        description: formData.description || null,
        responsible_team: formData.responsible_team || null,
        group: formData.group || null,
        is_active: formData.is_active,
        case_detail_visible_role_types: normalizeVisibleRoleTypes(formData.case_detail_visible_role_types),
        updated_at: new Date().toISOString(),
      };

      if (phaseModal.phase?.id) {
        const { error } = await supabase
          .from('phase_templates')
          .update(payload)
          .eq('id', phaseModal.phase.id);
        if (error) throw error;
        showToast('Phase modifiée avec succès');
      } else {
        const { error } = await supabase.from('phase_templates').insert(payload);
        if (error) throw error;
        showToast('Phase créée avec succès');
      }
      setPhaseModal({ isOpen: false, phase: null });
      await fetchAll();
    } catch (err) {
      showToast(err.message || 'Une erreur est survenue', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePhase = async () => {
    if (!deletePhaseModal.phase) return;
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('phase_templates')
        .delete()
        .eq('id', deletePhaseModal.phase.id);
      if (error) throw error;
      showToast('Phase supprimée avec succès');
      setDeletePhaseModal({ isOpen: false, phase: null });
      await fetchAll();
    } catch (err) {
      showToast(err.message || 'Une erreur est survenue', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDuplicatePhaseChoice = async (includeMilestones) => {
    const sourcePhase = duplicatePhaseModal.phase;
    if (!sourcePhase?.id) return;
    setDuplicatePhaseModal({ isOpen: false, phase: null });
    setIsSaving(true);
    try {
      const maxOrder = Math.max(0, ...phases.map((p) => Number(p.phase_order) || 0));
      const nextOrder = maxOrder + 1;
      const payload = {
        phase_name: phaseNameForDuplicate(sourcePhase.phase_name),
        phase_order: nextOrder,
        description: sourcePhase.description || null,
        responsible_team: sourcePhase.responsible_team || null,
        group: sourcePhase.group ?? null,
        is_active: sourcePhase.is_active !== false,
        case_detail_visible_role_types: normalizeVisibleRoleTypes(sourcePhase.case_detail_visible_role_types),
        updated_at: new Date().toISOString(),
      };

      const { data: newPhaseRow, error: phaseErr } = await supabase
        .from('phase_templates')
        .insert(payload)
        .select('id')
        .single();
      if (phaseErr) throw phaseErr;
      const newPhaseId = newPhaseRow.id;

      if (includeMilestones) {
        const srcMilestones = milestones
          .filter((m) => m.phase_template_id === sourcePhase.id)
          .sort((a, b) => (a.milestone_order ?? 0) - (b.milestone_order ?? 0));

        if (srcMilestones.length > 0) {
          const now = new Date().toISOString();
          const milestoneInserts = srcMilestones.map((m) => ({
            phase_template_id: newPhaseId,
            milestone_name: m.milestone_name,
            milestone_order: m.milestone_order,
            description: m.description || null,
            is_active: m.is_active !== false,
            case_detail_visible_role_types: normalizeVisibleRoleTypes(m.case_detail_visible_role_types),
            updated_at: now,
          }));

          const { data: newMilestones, error: msErr } = await supabase
            .from('milestone_templates')
            .insert(milestoneInserts)
            .select('id, milestone_order');
          if (msErr) throw msErr;

          const oldIds = srcMilestones.map((m) => m.id);
          const { data: allActions, error: actFetchErr } = await supabase
            .from('milestone_template_actions')
            .select('milestone_template_id, action_key, sort_order, config, allowed_role_types')
            .in('milestone_template_id', oldIds);
          if (actFetchErr) throw actFetchErr;

          if (allActions?.length) {
            const oldsByOrder = {};
            srcMilestones.forEach((m) => {
              const o = m.milestone_order ?? 0;
              if (!oldsByOrder[o]) oldsByOrder[o] = [];
              oldsByOrder[o].push(m.id);
            });
            const newsByOrder = {};
            (newMilestones || []).forEach((nm) => {
              const o = nm.milestone_order ?? 0;
              if (!newsByOrder[o]) newsByOrder[o] = [];
              newsByOrder[o].push(nm.id);
            });
            const idMap = {};
            Object.keys(oldsByOrder).forEach((oKey) => {
              const o = Number(oKey);
              const olds = oldsByOrder[o];
              const news = newsByOrder[o] || [];
              olds.forEach((oldId, i) => {
                if (news[i]) idMap[oldId] = news[i];
              });
            });

            const actionPayload = allActions
              .filter((a) => idMap[a.milestone_template_id])
              .map((a) => ({
                milestone_template_id: idMap[a.milestone_template_id],
                action_key: a.action_key,
                sort_order: a.sort_order ?? 0,
                config: a.config ?? {},
                allowed_role_types: a.allowed_role_types?.length
                  ? a.allowed_role_types
                  : ['administrator'],
                updated_at: now,
              }));

            if (actionPayload.length > 0) {
              const { error: actInsErr } = await supabase
                .from('milestone_template_actions')
                .insert(actionPayload);
              if (actInsErr) throw actInsErr;
            }
          }
        }
      }

      showToast(
        includeMilestones
          ? 'Phase dupliquée avec ses jalons et liaisons workflow'
          : 'Phase dupliquée (sans jalons)'
      );
      await fetchAll();
    } catch (err) {
      showToast(err.message || 'Erreur lors de la duplication', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Milestone CRUD ────────────────────────────────────────────────────
  const handleSaveMilestone = async (formData) => {
    setIsSaving(true);
    try {
      const selectedOptions = nameOptionsCatalog.filter((o) =>
        formData.selectedOptionIds.includes(o.id)
      );
      if (!selectedOptions.length) {
        throw new Error('Sélectionnez au moins une valeur autorisée.');
      }

      let milestoneName = String(formData.milestone_name || '').trim();
      if (selectedOptions.length === 1) {
        milestoneName = selectedOptions[0].label;
      } else if (!milestoneName) {
        throw new Error('Libellé générique requis pour plusieurs valeurs autorisées.');
      }

      const payload = {
        phase_template_id: formData.phase_template_id,
        milestone_name: milestoneName,
        milestone_order: formData.milestone_order,
        description: formData.description || null,
        is_active: formData.is_active,
        case_detail_visible_role_types: normalizeVisibleRoleTypes(formData.case_detail_visible_role_types),
        updated_at: new Date().toISOString(),
      };

      let milestoneId = milestoneModal.milestone?.id;

      if (milestoneId) {
        const { error } = await supabase
          .from('milestone_templates')
          .update(payload)
          .eq('id', milestoneId);
        if (error) throw error;
        showToast('Jalon modifié avec succès');
      } else {
        const { data, error } = await supabase
          .from('milestone_templates')
          .insert(payload)
          .select('id')
          .single();
        if (error) throw error;
        milestoneId = data.id;
        showToast('Jalon créé avec succès');
      }

      await replaceMilestoneTemplateNameOptions(milestoneId, formData.selectedOptionIds);
      setMilestoneModal({ isOpen: false, milestone: null });
      await fetchAll();
    } catch (err) {
      showToast(err.message || 'Une erreur est survenue', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteMilestone = async () => {
    if (!deleteMilestoneModal.milestone) return;
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('milestone_templates')
        .delete()
        .eq('id', deleteMilestoneModal.milestone.id);
      if (error) throw error;
      showToast('Jalon supprimé avec succès');
      setDeleteMilestoneModal({ isOpen: false, milestone: null });
      await fetchAll();
    } catch (err) {
      showToast(err.message || 'Une erreur est survenue', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Filtered data ─────────────────────────────────────────────────────
  const filteredPhases = phases.filter(
    (p) =>
      p.phase_name.toLowerCase().includes(search.toLowerCase()) ||
      (p.responsible_team || '').toLowerCase().includes(search.toLowerCase()) ||
      (p.group || '').toLowerCase().includes(search.toLowerCase())
  );

  const filteredMilestones = milestones.filter(
    (m) =>
      m.milestone_name.toLowerCase().includes(search.toLowerCase()) ||
      (m.phase?.phase_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const journeyFilteredPhases = useMemo(() => {
    const groupSortKey = (p) => {
      const raw = p.group;
      if (raw == null || String(raw).trim() === '') return { empty: true, str: '' };
      const s = String(raw).trim();
      const n = Number(s);
      if (!Number.isNaN(n) && s !== '') return { empty: false, num: n, str: s };
      return { empty: false, str: s };
    };
    const compareGroup = (a, b) => {
      const ka = groupSortKey(a);
      const kb = groupSortKey(b);
      if (ka.empty && kb.empty) return 0;
      if (ka.empty) return 1;
      if (kb.empty) return -1;
      if (ka.num != null && kb.num != null) return ka.num - kb.num;
      if (ka.num != null) return -1;
      if (kb.num != null) return 1;
      return ka.str.localeCompare(kb.str, undefined, { numeric: true, sensitivity: 'base' });
    };
    const sorted = [...phases].sort((a, b) => {
      const g = compareGroup(a, b);
      if (g !== 0) return g;
      return (a.phase_order ?? 0) - (b.phase_order ?? 0);
    });
    const q = search.toLowerCase().trim();
    if (!q) return sorted;
    return sorted.filter((p) => {
      const phaseHit =
        p.phase_name.toLowerCase().includes(q) ||
        (p.responsible_team || '').toLowerCase().includes(q) ||
        String(p.group || '')
          .toLowerCase()
          .includes(q) ||
        (p.description || '').toLowerCase().includes(q);
      if (phaseHit) return true;
      const ms = milestones.filter((m) => m.phase_template_id === p.id);
      return ms.some(
        (m) =>
          m.milestone_name.toLowerCase().includes(q) || (m.description || '').toLowerCase().includes(q)
      );
    });
  }, [phases, milestones, search]);

  const rowActionsClass =
    'flex items-center justify-end gap-1 flex-shrink-0 sm:flex-nowrap flex-wrap';

  // ── Breadcrumb ────────────────────────────────────────────────────────
  const breadcrumbItems = [
    { label: 'Tableau de bord', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Modèles de phases', path: '/phase-templates', icon: 'GitBranch' },
  ];

  // ── Loading ───────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Chargement des modèles...</p>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Breadcrumb items={breadcrumbItems} />

        <div className="px-6 py-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Workflow des commandes</h1>
              <p className="text-muted-foreground">
                Configurez les phases et jalons utilisés lors de la création de nouveaux cas
              </p>
              <p className="text-sm text-muted-foreground mt-2 max-w-3xl leading-relaxed">
                L’<strong className="text-foreground font-medium">ordre des phases</strong> et des{' '}
                <strong className="text-foreground font-medium">jalons</strong> définit le parcours copié dans chaque{' '}
                <strong className="text-foreground font-medium">nouveau cas</strong>. Les actions visibles sur la fiche
                cas (workflow) se règlent par jalon dans la vue Parcours ou dans l’onglet Workflow.
              </p>
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 border border-primary/20 rounded-full">
                  <Icon name="GitBranch" size={14} className="text-primary" />
                  <span className="text-sm font-medium text-primary">{phases.length} phases</span>
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-secondary/10 border border-secondary/20 rounded-full">
                  <Icon name="Flag" size={14} className="text-primary" />
                  <span className="text-sm font-medium text-primary">{milestones.length} jalons</span>
                </div>
              </div>
            </div>
            {viewMode !== 'workflow' && (
              <Button
                variant="default"
                size="lg"
                iconName="Plus"
                iconPosition="left"
                onClick={() =>
                  viewMode === 'journey' || (viewMode === 'lists' && listSubTab === 'phases')
                    ? setPhaseModal({ isOpen: true, phase: null })
                    : setMilestoneModal({ isOpen: true, milestone: null })
                }
              >
                {viewMode === 'journey' || (viewMode === 'lists' && listSubTab === 'phases')
                  ? 'Nouvelle phase'
                  : 'Nouveau jalon'}
              </Button>
            )}
          </div>

          {/* Tabs + Search */}
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
            <div className="flex flex-col gap-2 w-full lg:w-auto">
              <div className="flex items-center gap-1 bg-muted/40 border border-border rounded-lg p-1 flex-wrap">
                <button
                  type="button"
                  onClick={() => {
                    setViewMode('journey');
                    setSearch('');
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'journey'
                      ? 'bg-card text-foreground shadow-sm border border-border'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Route" size={15} />
                  Parcours
                  <span className="ml-1 px-1.5 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                    {phases.length}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setViewMode('lists');
                    setSearch('');
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'lists'
                      ? 'bg-card text-foreground shadow-sm border border-border'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Table2" size={15} />
                  Listes détaillées
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setViewMode('workflow');
                    setSearch('');
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'workflow'
                      ? 'bg-card text-foreground shadow-sm border border-border'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Workflow" size={15} />
                  Workflow
                  <span className="ml-1 px-1.5 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                    {milestones.length}
                  </span>
                </button>
              </div>
              {viewMode === 'lists' && (
                <div className="flex items-center gap-2 flex-wrap text-sm pl-1">
                  <span className="text-muted-foreground">Afficher :</span>
                  <div className="flex rounded-md border border-border bg-background p-0.5">
                    <button
                      type="button"
                      onClick={() => setListSubTab('phases')}
                      className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                        listSubTab === 'phases'
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Phases (tableau)
                    </button>
                    <button
                      type="button"
                      onClick={() => setListSubTab('milestones')}
                      className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                        listSubTab === 'milestones'
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Jalons (tableau)
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="relative w-full lg:w-auto">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                className={`${inputClass} pl-9 w-full lg:w-72`}
                placeholder={
                  viewMode === 'journey'
                    ? 'Rechercher une phase ou un jalon…'
                    : viewMode === 'lists' && listSubTab === 'phases'
                      ? 'Rechercher une phase…'
                      : viewMode === 'lists' && listSubTab === 'milestones'
                        ? 'Rechercher un jalon…'
                        : 'Rechercher un jalon (workflow)…'
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* ── PARCOURS (phases → jalons → workflow) ── */}
          {viewMode === 'journey' && (
            <div className="space-y-4">
              {phases.length === 0 ? (
                <div className="text-center py-16 px-4 border border-dashed border-border rounded-xl bg-muted/10">
                  <Icon name="Route" size={44} className="mx-auto mb-4 text-muted-foreground/40" />
                  <p className="text-foreground font-medium mb-1">Aucune phase pour l’instant</p>
                  <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                    Commencez par créer une phase : elle apparaîtra ici dans l’ordre du parcours, puis ajoutez des
                    jalons sous chaque phase.
                  </p>
                  <Button
                    variant="default"
                    size="lg"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => setPhaseModal({ isOpen: true, phase: null })}
                  >
                    Créer la première phase
                  </Button>
                </div>
              ) : journeyFilteredPhases.length === 0 ? (
                <div className="text-center py-12 border border-border rounded-xl bg-card">
                  <Icon name="Search" size={40} className="mx-auto mb-3 text-muted-foreground/40" />
                  <p className="text-muted-foreground">Aucun résultat pour cette recherche.</p>
                </div>
              ) : (
                journeyFilteredPhases.map((phase) => {
                  const phaseMilestones = milestones
                    .filter((m) => m.phase_template_id === phase.id)
                    .sort((a, b) => (a.milestone_order ?? 0) - (b.milestone_order ?? 0));
                  return (
                    <div
                      key={phase.id}
                      className="bg-card border border-border rounded-xl overflow-hidden shadow-sm"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 p-4 sm:p-5 border-b border-border bg-muted/20">
                        <div className="flex gap-3 min-w-0 flex-1">
                          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/15 text-primary text-sm font-bold flex-shrink-0">
                            {phase.phase_order}
                          </span>
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <h2 className="text-lg font-semibold text-foreground">{phase.phase_name}</h2>
                              <ActiveBadge active={phase.is_active} />
                              <GroupBadge group={phase.group} />
                            </div>
                            {phase.description && (
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{phase.description}</p>
                            )}
                          </div>
                        </div>
                        <div className={rowActionsClass}>
                          <button
                            type="button"
                            onClick={() =>
                              setMilestoneModal({ isOpen: true, milestone: { phase_template_id: phase.id } })
                            }
                            className="p-2 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors border border-border/60 hover:border-primary/30"
                            title="Ajouter un jalon à cette phase"
                          >
                            <Icon name="FlagPlus" size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDuplicatePhaseModal({ isOpen: true, phase })}
                            className="p-2 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors border border-border/60 hover:border-primary/30"
                            title="Dupliquer la phase"
                          >
                            <Icon name="Copy" size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => setPhaseModal({ isOpen: true, phase })}
                            className="p-2 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors border border-border/60 hover:border-primary/30"
                            title="Modifier la phase"
                          >
                            <Icon name="Pencil" size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeletePhaseModal({ isOpen: true, phase })}
                            className="p-2 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors border border-border/60 hover:border-destructive/30"
                            title="Supprimer la phase"
                          >
                            <Icon name="Trash2" size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="p-3 sm:p-4">
                        {phaseMilestones.length === 0 ? (
                          <div className="text-center py-8 px-4 border border-dashed border-border rounded-lg bg-muted/5">
                            <p className="text-sm text-muted-foreground mb-4">
                              Aucun jalon dans cette étape du parcours.
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              iconName="FlagPlus"
                              iconPosition="left"
                              onClick={() =>
                                setMilestoneModal({ isOpen: true, milestone: { phase_template_id: phase.id } })
                              }
                            >
                              Ajouter un jalon
                            </Button>
                          </div>
                        ) : (
                          <ul className="space-y-2">
                            {phaseMilestones.map((m) => (
                              <li
                                key={m.id}
                                className="flex flex-col gap-3 p-3 sm:p-4 rounded-lg border border-border bg-background/60"
                              >
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                                  <div className="flex items-start gap-3 min-w-0 flex-1">
                                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-secondary/25 text-secondary-foreground text-xs font-bold flex-shrink-0">
                                      {m.milestone_order}
                                    </span>
                                    <div className="min-w-0">
                                      <div className="font-medium text-foreground">{m.milestone_name}</div>
                                      {m.description && (
                                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                          {m.description}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-2 pl-11 sm:pl-0">
                                    <ActiveBadge active={m.is_active} />
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-500/10 text-violet-700 dark:text-violet-300 border border-violet-500/25">
                                      {actionBindingCounts[m.id] ?? 0} action(s)
                                    </span>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      iconName="Settings2"
                                      iconPosition="left"
                                      onClick={() =>
                                        setWorkflowModal({
                                          isOpen: true,
                                          milestone: m,
                                          phaseLabel: phase.phase_name || '',
                                        })
                                      }
                                    >
                                      Workflow
                                    </Button>
                                    <button
                                      type="button"
                                      onClick={() => setMilestoneModal({ isOpen: true, milestone: m })}
                                      className="p-2 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 border border-border/60"
                                      title="Modifier le jalon"
                                    >
                                      <Icon name="Pencil" size={15} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setDeleteMilestoneModal({ isOpen: true, milestone: m })}
                                      className="p-2 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 border border-border/60"
                                      title="Supprimer le jalon"
                                    >
                                      <Icon name="Trash2" size={15} />
                                    </button>
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* ── LISTES : PHASES (tableau) ── */}
          {viewMode === 'lists' && listSubTab === 'phases' && (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-16">#</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Nom de la phase</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">Groupe</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">Jalons</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Statut</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredPhases.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-muted-foreground">
                        <Icon name="GitBranch" size={40} className="mx-auto mb-3 opacity-30" />
                        <p>Aucune phase trouvée</p>
                      </td>
                    </tr>
                  ) : (
                    filteredPhases.map((phase) => {
                      const phaseMilestones = milestones.filter((m) => m.phase_template_id === phase.id);
                      const isExpanded = expandedPhaseId === phase.id;
                      return (
                        <React.Fragment key={phase.id}>
                          <tr className="hover:bg-muted/20 transition-colors group">
                            {/* Order */}
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-bold">
                                {phase.phase_order}
                              </span>
                            </td>
                            {/* Name + description */}
                            <td className="px-4 py-3">
                              <div className="font-medium text-foreground">{phase.phase_name}</div>
                              {phase.description && (
                                <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{phase.description}</div>
                              )}
                            </td>
                            {/* Group */}
                            <td className="px-4 py-3 hidden md:table-cell">
                              <GroupBadge group={phase.group} />
                            </td>
                            {/* Milestones count */}
                            <td className="px-4 py-3 hidden lg:table-cell">
                              <button
                                onClick={() => setExpandedPhaseId(isExpanded ? null : phase.id)}
                                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                              >
                                <Icon name="Flag" size={13} />
                                {phaseMilestones.length} jalon{phaseMilestones.length !== 1 ? 's' : ''}
                                <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={13} />
                              </button>
                            </td>
                            {/* Status */}
                            <td className="px-4 py-3">
                              <ActiveBadge active={phase.is_active} />
                            </td>
                            {/* Actions */}
                            <td className="px-4 py-3 text-right">
                              <div className={rowActionsClass}>
                                <button
                                  type="button"
                                  onClick={() => setMilestoneModal({ isOpen: true, milestone: { phase_template_id: phase.id } })}
                                  className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors border border-border/50"
                                  title="Ajouter un jalon"
                                >
                                  <Icon name="FlagPlus" size={15} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setDuplicatePhaseModal({ isOpen: true, phase })}
                                  className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors border border-border/50"
                                  title="Dupliquer la phase"
                                >
                                  <Icon name="Copy" size={15} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setPhaseModal({ isOpen: true, phase })}
                                  className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors border border-border/50"
                                  title="Modifier la phase"
                                >
                                  <Icon name="Pencil" size={15} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setDeletePhaseModal({ isOpen: true, phase })}
                                  className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors border border-border/50"
                                  title="Supprimer la phase"
                                >
                                  <Icon name="Trash2" size={15} />
                                </button>
                              </div>
                            </td>
                          </tr>
                          {/* Expanded milestones */}
                          {isExpanded && (
                            <tr>
                              <td colSpan={6} className="bg-muted/10 border-t border-dashed border-border px-4 py-3">
                                {phaseMilestones.length === 0 ? (
                                  <p className="text-sm text-muted-foreground text-center py-2">
                                    Aucun jalon pour cette phase.{' '}
                                    <button
                                      className="text-primary hover:underline"
                                      onClick={() => setMilestoneModal({ isOpen: true, milestone: { phase_template_id: phase.id } })}
                                    >
                                      En ajouter un
                                    </button>
                                  </p>
                                ) : (
                                  <div className="space-y-1.5 pl-8">
                                    {phaseMilestones
                                      .sort((a, b) => a.milestone_order - b.milestone_order)
                                      .map((m) => (
                                        <div
                                          key={m.id}
                                          className="flex items-center justify-between bg-card border border-border rounded-md px-3 py-2 group/m"
                                        >
                                          <div className="flex items-center gap-3">
                                            <span className="text-xs font-bold text-muted-foreground w-5 text-center">
                                              {m.milestone_order}
                                            </span>
                                            <div>
                                              <div className="text-sm font-medium text-foreground">{m.milestone_name}</div>
                                              {m.description && (
                                                <div className="text-xs text-muted-foreground">{m.description}</div>
                                              )}
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-2 flex-shrink-0">
                                            <ActiveBadge active={m.is_active} />
                                            <button
                                              type="button"
                                              onClick={() => setMilestoneModal({ isOpen: true, milestone: m })}
                                              className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors border border-border/50"
                                              title="Modifier le jalon"
                                            >
                                              <Icon name="Pencil" size={13} />
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => setDeleteMilestoneModal({ isOpen: true, milestone: m })}
                                              className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors border border-border/50"
                                              title="Supprimer le jalon"
                                            >
                                              <Icon name="Trash2" size={13} />
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ── LISTES : JALONS (tableau) ── */}
          {viewMode === 'lists' && listSubTab === 'milestones' && (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-16">#</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Nom du jalon</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">Phase parente</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">Description</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Statut</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredMilestones.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-muted-foreground">
                        <Icon name="Flag" size={40} className="mx-auto mb-3 opacity-30" />
                        <p>Aucun jalon trouvé</p>
                      </td>
                    </tr>
                  ) : (
                    filteredMilestones.map((m) => (
                      <tr key={m.id} className="hover:bg-muted/20 transition-colors group">
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-secondary/20 text-secondary-foreground text-sm font-bold">
                            {m.milestone_order}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-foreground">{m.milestone_name}</div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          {m.phase ? (
                            <span className="inline-flex items-center gap-1.5 text-sm">
                              <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">
                                {m.phase.phase_order}
                              </span>
                              <span className="text-foreground">{m.phase.phase_name}</span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <span className="text-sm text-muted-foreground line-clamp-1">
                            {m.description || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <ActiveBadge active={m.is_active} />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className={rowActionsClass}>
                            <button
                              type="button"
                              onClick={() => setMilestoneModal({ isOpen: true, milestone: m })}
                              className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors border border-border/50"
                              title="Modifier le jalon"
                            >
                              <Icon name="Pencil" size={15} />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteMilestoneModal({ isOpen: true, milestone: m })}
                              className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors border border-border/50"
                              title="Supprimer le jalon"
                            >
                              <Icon name="Trash2" size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ── WORKFLOW (vue plate, tous les jalons) ── */}
          {viewMode === 'workflow' && (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-16">#</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Jalon</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">Phase</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions liées</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Configurer</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredMilestones.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-12 text-muted-foreground">
                        <Icon name="Workflow" size={40} className="mx-auto mb-3 opacity-30" />
                        <p>
                          Aucun jalon — créez-en depuis la vue <strong className="text-foreground">Parcours</strong> ou
                          l’onglet <strong className="text-foreground">Listes détaillées</strong> → Jalons.
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filteredMilestones.map((m) => (
                      <tr key={m.id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-secondary/20 text-secondary-foreground text-sm font-bold">
                            {m.milestone_order}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-foreground">{m.milestone_name}</div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-sm text-foreground">
                          {m.phase?.phase_name || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                            {actionBindingCounts[m.id] ?? 0} action(s)
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="Settings2"
                            iconPosition="left"
                            onClick={() =>
                              setWorkflowModal({
                                isOpen: true,
                                milestone: m,
                                phaseLabel: m.phase?.phase_name || '',
                              })
                            }
                          >
                            Workflow
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* ── Modals ── */}
      <PhaseFormModal
        isOpen={phaseModal.isOpen}
        onClose={() => setPhaseModal({ isOpen: false, phase: null })}
        onSave={handleSavePhase}
        phase={phaseModal.phase}
        isLoading={isSaving}
        workflowRoleOptions={workflowRoleOptions}
      />

      <DeleteModal
        isOpen={deletePhaseModal.isOpen}
        onClose={() => setDeletePhaseModal({ isOpen: false, phase: null })}
        onConfirm={handleDeletePhase}
        itemName={deletePhaseModal.phase?.phase_name || ''}
        isLoading={isSaving}
      />

      <DuplicatePhaseModal
        isOpen={duplicatePhaseModal.isOpen}
        onClose={() => setDuplicatePhaseModal({ isOpen: false, phase: null })}
        phase={duplicatePhaseModal.phase}
        onChoose={handleDuplicatePhaseChoice}
        isLoading={isSaving}
      />

      <MilestoneFormModal
        isOpen={milestoneModal.isOpen}
        onClose={() => setMilestoneModal({ isOpen: false, milestone: null })}
        onSave={handleSaveMilestone}
        milestone={milestoneModal.milestone}
        phases={phases}
        isLoading={isSaving}
        workflowRoleOptions={workflowRoleOptions}
        nameOptionsCatalog={nameOptionsCatalog}
      />

      <DeleteModal
        isOpen={deleteMilestoneModal.isOpen}
        onClose={() => setDeleteMilestoneModal({ isOpen: false, milestone: null })}
        onConfirm={handleDeleteMilestone}
        itemName={deleteMilestoneModal.milestone?.milestone_name || ''}
        isLoading={isSaving}
      />

      <MilestoneWorkflowModal
        isOpen={workflowModal.isOpen}
        onClose={() => setWorkflowModal({ isOpen: false, milestone: null, phaseLabel: '' })}
        milestone={workflowModal.milestone}
        phaseLabel={workflowModal.phaseLabel}
        workflowRoleOptions={workflowRoleOptions}
        onSaved={() => {
          fetchAll();
          showToast('Workflow enregistré');
        }}
      />

      {/* ── Toast notification ── */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-[100] flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border text-sm font-medium animate-slide-in ${
            toast.type === 'error'
              ? 'bg-destructive/10 border-destructive/20 text-destructive'
              : 'bg-success/10 border-success/20 text-success'
          }`}
        >
          <Icon name={toast.type === 'error' ? 'AlertCircle' : 'CheckCircle2'} size={18} />
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default PhaseTemplatesPage;